import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import React from 'react';
import initMockedData from '../mock/initMockedData';
import history from './history';
import { getMockedLink } from '../mock/mockedLink';

const ApolloClientContext = React.createContext<ApolloClient | undefined>(
  undefined,
);

const referrerPolicy = 'no-referrer-when-downgrade';

const redirectToLoginPage = (response: Response) => {
  const currentLocation = history.location;
  const location = response?.headers?.get('Location');

  if (location) {
    window.location.replace(location);
  } else if (currentLocation.pathname === '/') {
    history.push('/login');
  }
};

// Use a simple merge strategy that ensures that we keep referential stability when possible
const simpleMerge = (existing: any, incoming: any) => ({
  ...existing,
  ...incoming,
});

const getInMemoryCache = () =>
  new InMemoryCache({
    typePolicies: {
      // This is needed to avoid re-rendering loop and extra requests
      DocumentQuery: { merge: simpleMerge },
      ContentQuery: { merge: simpleMerge },
      NotificationQuery: { merge: simpleMerge },

      Document: {
        fields: {
          sharedWith: {
            merge: (existing, incoming) => incoming,
          },
        },
      },
    },
  });

const errorLink = new ErrorLink(({ error }) => {
  if (
    CombinedGraphQLErrors.is(error) &&
    error?.errors.some(({ extensions }) => extensions?.code === 'Unauthorized')
  ) {
    console.error('Unauthorized');
  } else if (
    ServerError.is(error) &&
    error?.statusCode === 401 &&
    error?.response?.headers?.has('Location') === true
  ) {
    redirectToLoginPage(error.response);
  } else {
    console.error('GraphQL Error:', error);
  }
});

function getHttpLink() {
  return new HttpLink({
    credentials: 'include',
    uri: ({ operationName, operationType }) =>
      `${window.__APP_CONFIG__.KIWI_URL}/graphql?${operationType}=${operationName}`,
    headers: { 'X-Request-With': 'XMLHttpRequest' },
    fetchOptions: {
      referrerPolicy,
    },
  });
}

async function getLink(): Promise<ApolloLink> {
  return errorLink.concat(
    import.meta.env.VITE_USE_MOCK_SCHEMA
      ? await getMockedLink({ redirectToLoginPage, referrerPolicy })
      : getHttpLink(),
  );
}

const initGraphql = async (): Promise<ApolloClient> => {
  if (import.meta.env.VITE_USE_MOCK_SCHEMA) {
    await initMockedData();
  }

  return new ApolloClient({
    link: await getLink(),
    cache: getInMemoryCache(),
  });
};

export { ApolloClientContext, initGraphql };
